/**
 * @file notesLanguage.ts
 * @brief The Meeting Notes language last chosen, and the Notes page's
 *        remembered tab, per browser.
 *
 * @details
 * DES 07-10-2026 (web port of the phone's notesLanguage.ts): slide on the
 * Notes page, then Setup, then slide again was a double action. The Meeting
 * notes tab now starts the recorder directly; the only Setup choice Notes
 * needs (the language the summary is written in) is remembered here when
 * Setup starts a Notes meeting. Best-effort: storage can be blocked (private
 * window, cleared site data), and a failed read is English (UK), the Setup
 * default.
 *
 * `useLanguageLabel` reads the same language catalogue LanguagePicker loads
 * (same query key, same parsed shape), so the name shown on the Notes page
 * is the name picked in Setup, without a second request.
 *
 * @references
 * - FRONTEND/src/screens/Notes/notesLanguage.ts (the phone original)
 * - ./NotesPage.tsx (reads both), ../employee-boardroom/BoardroomSetupPage.tsx
 *   (writes the language)
 *
 * @author DES
 * @date 07-10-2026
 */

import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet } from "@/shared/api/client";
import { scheduleEndpoints } from "@/shared/api/endpoints";

const LANGUAGE_KEY = "vz.notes.meetingNotesLanguage";
const TAB_KEY = "vz.notes.libraryTab";

export const DEFAULT_NOTES_LANGUAGE = "en-GB";

/** Meeting notes | Notes | Agendas (agendas: DES 12-10-2026). */
export type NotesLibraryTab = "meetings" | "personal" | "agendas";

export function loadNotesLanguage(): string {
  try {
    const v = window.localStorage.getItem(LANGUAGE_KEY);
    return v && v.trim() ? v : DEFAULT_NOTES_LANGUAGE;
  } catch {
    return DEFAULT_NOTES_LANGUAGE;
  }
}

export function saveNotesLanguage(code: string | undefined): void {
  if (!code) return;
  try {
    window.localStorage.setItem(LANGUAGE_KEY, code);
  } catch {
    /* best effort */
  }
}

/** Personal until a choice was saved: what the page showed before the tabs. */
export function loadNotesLibraryTab(): NotesLibraryTab {
  try {
    const v = window.localStorage.getItem(TAB_KEY);
    return v === "meetings" || v === "agendas" ? v : "personal";
  } catch {
    return "personal";
  }
}

export function saveNotesLibraryTab(tab: NotesLibraryTab): void {
  try {
    window.localStorage.setItem(TAB_KEY, tab);
  } catch {
    /* best effort */
  }
}

// LanguagePicker's catalogue query: same key and shape, so they share a cache.
const LanguagesSchema = z.object({
  languages: z.array(
    z.object({ code: z.string(), label: z.string(), locale: z.string().optional() }),
  ),
});

/** The language's display name ("English (UK)"); the code until it loads. */
export function useLanguageLabel(code: string): string {
  const q = useQuery({
    queryKey: ["s2s", "languages"],
    queryFn: async () => LanguagesSchema.parse(await apiGet<unknown>(scheduleEndpoints.languages)).languages,
    staleTime: 5 * 60_000,
  });
  const match = (q.data ?? []).find(
    (l) => l.locale === code || l.code === code || (l.locale || l.code) === code,
  );
  if (match) return match.label;
  return code === DEFAULT_NOTES_LANGUAGE ? "English (UK)" : code;
}
